import { KeyboardEvent, ReactNode, useCallback } from 'react';
import { Icon } from '../icon/Icon';
import { keyDownHelper } from '../../helpers/keyDownHelper';
import { getClassNames } from '../../helpers/classHelpers';
import { AdaptiveIconProps, IconTheme } from '../icon/Icon.types';
import styles from './PromptCard.module.scss';
import { getTruncatedTextByWord } from '../../helpers/stringHelpers';
import ChipList from '../chip-list/ChipList';
import { Status } from '../../helpers/statusHelpers';

type CardProps = {
    id?: string;
    className?: string;
    iconClassName?: string;
    titleClassName?: string;
    icon?: Partial<AdaptiveIconProps> & Required<Pick<AdaptiveIconProps, 'iconName'>>;
    title?: string;
    description?: string;
    rightSide?: ReactNode;
    dataAttributes?: Record<string, string>;

    isDisabled?: boolean;
    inverseHoverEffect?: boolean;
    isActive?: boolean;
    maxDescriptionLength?: number;
    chipList?: string[];
    onClick?: (card: Exclude<CardProps, 'onClick'>) => void;
};

const PromptCard = ({ id, onClick, className, titleClassName, iconClassName, dataAttributes, isDisabled, inverseHoverEffect, ...props }: CardProps) => {
    const { icon, description, isActive, title, rightSide, maxDescriptionLength, chipList } = props ?? {};

    const onClickCard = useCallback(() => onClick?.(props), [onClick, props]);
    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLElement>) => onClick && keyDownHelper(e, onClickCard), [onClick, onClickCard]);

    const truncatedDescription = getTruncatedTextByWord(description ?? '', maxDescriptionLength);
    const hasChips = !!chipList && chipList.length > 0;

    return (
        <div
            id={id}
            tabIndex={!!onClick ? 0 : undefined}
            {...dataAttributes}
            className={getClassNames([
                className,
                styles.card,
                isActive && styles.selected,
                onClick && styles.clickable,
                isDisabled && styles.disabled,
                inverseHoverEffect ? styles['inverse-hover'] : styles['normal-hover'],
                hasChips ? styles['has-chips'] : styles['no-chips']
            ])}
            onKeyDown={handleKeyDown}
            onClick={onClickCard}
        >
            {icon && (
                <div className={iconClassName}>
                    <Icon.Adaptive size={42} theme={IconTheme.accent} {...icon} title="" />
                </div>
            )}

            {(title || description) && (
                <div className={styles.info}>
                    {title && <b className={getClassNames([titleClassName, styles.title])}>{title}</b>}
                    {description && <p className={styles.description}>{truncatedDescription}</p>}
                    {hasChips && (
                        <div className={styles['chip-list-wrapper']}>
                            <ChipList
                                chips={chipList.map(label => ({
                                    label: label.charAt(0).toUpperCase() + label.slice(1),
                                    theme: 'light' as Status
                                }))}
                                minChipWidth={30}
                            />
                        </div>
                    )}
                </div>
            )}

            {rightSide && <div>{rightSide}</div>}
        </div>
    );
};

export default PromptCard;

